import {
  normalizeTrainerId,
  getTrainerDefaultAssistantFields,
  getAssistantOwnDefaultRateEur,
} from "./trainer-assistant-honorar.js";
import { isFreeTrainingTrainer } from "./free-training-trainer.js";
import { filterTrainersForActivePlanning } from "./trainer-lifecycle.js";

/**
 * @param {Record<string, unknown>|null|undefined} trainer
 * @returns {boolean}
 */
export function isTrainerAssistantOnly(trainer) {
  return Boolean(trainer?.is_assistant_only);
}

/**
 * @param {Record<string, unknown>|null|undefined} trainer
 * @returns {boolean}
 */
export function isTrainerPrimaryPlanningEligible(trainer) {
  return !isTrainerAssistantOnly(trainer);
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @returns {Array<Record<string, unknown>>}
 */
/** Nur Rolle (kein Assistenz-only); für Terminplanung zusätzlich {@link filterTrainersForPrimaryPlanning} aus trainer-lifecycle. */
export function filterTrainersByPrimaryRole(trainers) {
  return (trainers || []).filter(isTrainerPrimaryPlanningEligible);
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @param {string|null|undefined} mainTrainerId
 * @param {{ includeTrainerIds?: string[] }} [opts]
 * @returns {Array<Record<string, unknown>>}
 */
export function filterTrainersForAssistantPlanning(
  trainers,
  mainTrainerId,
  opts = {},
) {
  const main = normalizeTrainerId(mainTrainerId);
  const include = new Set(
    (opts.includeTrainerIds || [])
      .map((id) => normalizeTrainerId(id))
      .filter(Boolean),
  );
  return (trainers || []).filter((tr) => {
    const id = normalizeTrainerId(tr?.id);
    if (!id || id === main) return false;
    if (include.has(id)) return true;
    if (Boolean(tr?.is_inactive) || isFreeTrainingTrainer(tr)) return false;
    return isTrainerAssistantOnly(tr);
  });
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @param {string|null|undefined} mainTrainerId
 * @returns {boolean}
 */
export function hasPlanningAssistantTrainerOptions(trainers, mainTrainerId) {
  return filterTrainersForAssistantPlanning(trainers, mainTrainerId).length > 0;
}

/**
 * Assistenten-Kandidaten für die Terminplanung: nur als Assistenztrainer markierte Profile.
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @param {string|null|undefined} mainTrainerId
 * @param {{ includeTrainerIds?: string[] }} [opts]
 */
export function filterTrainersForSessionAssistantPlanning(
  trainers,
  mainTrainerId,
  opts = {},
) {
  return filterTrainersForAssistantPlanning(
    filterTrainersForActivePlanning(trainers, opts),
    mainTrainerId,
    opts,
  );
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @param {string|null|undefined} mainTrainerId
 */
export function hasSessionAssistantTrainerOptions(trainers, mainTrainerId) {
  return (
    filterTrainersForSessionAssistantPlanning(trainers, mainTrainerId).length >
    0
  );
}

/**
 * Standard-Assistent eines Haupttrainers, nur wenn als Assistent (is_assistant_only) wählbar.
 * @param {Record<string, unknown>|null|undefined} trainer
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @param {string|null|undefined} mainTrainerId
 */
export function resolvePlanningAssistantDefaultsFromTrainer(
  trainer,
  trainers,
  mainTrainerId,
) {
  const def = getTrainerDefaultAssistantFields(trainer);
  const defId = normalizeTrainerId(def.assistantTrainerId);
  if (!defId) {
    return { assistantTrainerId: "", assistantHonorarEur: null };
  }
  const allowed = filterTrainersForSessionAssistantPlanning(
    trainers,
    mainTrainerId,
  );
  const ok = allowed.some((tr) => normalizeTrainerId(tr?.id) === defId);
  if (!ok) {
    return { assistantTrainerId: "", assistantHonorarEur: null };
  }
  // Fixer Betrag beim Haupttrainer hat Vorrang; ohne Override zählt der
  // Standard-Satz, den der Assistent selbst in seinen Stammdaten hinterlegt hat.
  const assistantHonorarEur =
    def.assistantHonorarEur != null
      ? def.assistantHonorarEur
      : getAssistantOwnDefaultRateEur(findTrainerById(trainers, defId));
  return {
    assistantTrainerId: defId,
    assistantHonorarEur,
  };
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @param {string|null|undefined} trainerId
 * @returns {Record<string, unknown>|null}
 */
export function findTrainerById(trainers, trainerId) {
  const tid = normalizeTrainerId(trainerId);
  if (!tid) return null;
  return (
    (trainers || []).find((tr) => normalizeTrainerId(tr?.id) === tid) || null
  );
}

/**
 * @param {string|null|undefined} mainTrainerId
 * @param {string|null|undefined} assistantTrainerId
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @returns {{ ok: true }|{ ok: false, code: "main-missing"|"same-trainer"|"main-assistant-only" }}
 */
export function validateSessionTrainerRoleAssignment(
  mainTrainerId,
  assistantTrainerId,
  trainers,
) {
  const main = normalizeTrainerId(mainTrainerId);
  const assistant = normalizeTrainerId(assistantTrainerId);

  if (!main) return { ok: false, code: "main-missing" };
  if (assistant && assistant === main)
    return { ok: false, code: "same-trainer" };

  const mainTr = findTrainerById(trainers, main);
  if (mainTr && !isTrainerPrimaryPlanningEligible(mainTr)) {
    return { ok: false, code: "main-assistant-only" };
  }

  return { ok: true };
}
